import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, IconButton, Menu, MenuItem, Box, Avatar } from '@mui/material';
import { AccountCircle, Home as HomeIcon, Login as LoginIcon } from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

const Navigation = () => {
  const history = useHistory();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);

  // 从全局状态获取 MongoDB 认证信息
  const {
    mongoUser,
    isLoggedInMongo,
    isLoggedInJson,
    logout,
    switchAuthSystem,
  } = useAuthStore();

  // 本地状态：显示用户名和头像
  const [displayName, setDisplayName] = useState('');
  const [userAvatar, setUserAvatar] = useState(null);

  // 统一的登录状态检查
  const isLoggedIn = isLoggedInMongo || isLoggedInJson;

  // 更新显示的用户名和头像
  useEffect(() => {
    if (isLoggedInMongo && mongoUser) {
      // MongoDB 系统用户
      setDisplayName(mongoUser.profile?.nickname || mongoUser.username || '用户');
      // 获取用户头像
      const avatar = mongoUser.profile?.avatar;
      setUserAvatar(avatar && avatar.trim() ? avatar : null);
    } else if (isLoggedInJson) {
      // 旧系统用户
      try {
        const jsonUser = localStorage.getItem('user');
        if (jsonUser) {
          const user = JSON.parse(jsonUser);
          setDisplayName(user.username || user.email || '用户');
          // 旧系统用户头像（如果有的话）
          const avatar = user.avatar || user.profile?.avatar;
          setUserAvatar(avatar && avatar.trim() ? avatar : null);
        }
      } catch (e) {
        setDisplayName('用户');
        setUserAvatar(null);
      }
    } else {
      setDisplayName('');
      setUserAvatar(null);
    }
  }, [isLoggedInMongo, isLoggedInJson, mongoUser, location]);

  // 打开用户菜单
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // 关闭用户菜单
  const handleClose = () => {
    setAnchorEl(null);
  };

  // 页面跳转
  const handleNavigate = (path) => {
    history.push(path);
    handleClose();
  };

  // 统一退出登录 - 同时清除两套系统的状态
  const handleLogout = async () => {
    try {
      // 尝试调用旧系统登出 API
      await fetch('/api/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      }).catch(() => {
        // 忽略网络错误，继续本地清理
      });
    } catch (error) {
      console.error('旧系统登出 API 调用失败:', error);
    }

    // 清除旧系统本地存储
    localStorage.removeItem('user');

    // 调用 authStore 的 logout 方法（清除 MongoDB 系统状态）
    logout();

    // 切换认证系统标记为 json（默认）
    switchAuthSystem('json');

    handleClose();

    // 退出后跳转到首页
    history.push('/');
  };

  // 跳转到登录页面
  const handleGoToLogin = () => {
    handleClose();
    history.push('/login-v2');
  };

  return (
    <AppBar position="static">
      <Toolbar sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {/* 左侧首页图标 */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="首页"
          onClick={() => history.push('/')}
          sx={{
            position: 'absolute',
            left: 16,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <HomeIcon sx={{ fontSize: 28 }} />
        </IconButton>

        {/* 中间标题 */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            cursor: 'pointer',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)'
          }}
          onClick={() => history.push('/')}
        >
          热门短剧
        </Typography>

        {/* 右侧用户区域 - 绝对定位到右侧 */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'absolute',
          right: 16
        }}>
          {/* 已登录状态：显示用户名 + 头像按钮 */}
          {isLoggedIn ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* 登录状态标识：MongoDB 系统显示徽标 */}
              {isLoggedInMongo && (
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    px: 1,
                    py: 0.3,
                    borderRadius: 1,
                    fontSize: '0.7rem'
                  }}
                >
                  新系统
                </Typography>
              )}
              {isLoggedInJson && !isLoggedInMongo && (
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    px: 1,
                    py: 0.3,
                    borderRadius: 1,
                    fontSize: '0.7rem'
                  }}
                >
                  旧系统
                </Typography>
              )}

              {/* 用户名 */}
              <Typography
                variant="body2"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  maxWidth: 100,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {displayName}
              </Typography>

              {/* 头像按钮 */}
              <IconButton
                size="large"
                aria-label="用户菜单"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                {userAvatar ? (
                  <Avatar
                    src={userAvatar}
                    alt={displayName}
                    sx={{
                      width: 28,
                      height: 28,
                      border: '2px solid rgba(255, 255, 255, 0.8)',
                      boxShadow: 1
                    }}
                  />
                ) : (
                  <AccountCircle sx={{ fontSize: 28 }} />
                )}
              </IconButton>
            </Box>
          ) : (
            /* 未登录状态：显示登录图标 */
            <IconButton
              color="inherit"
              onClick={handleGoToLogin}
              aria-label="登录"
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <LoginIcon sx={{ fontSize: 28 }} />
            </IconButton>
          )}

          {/* 用户下拉菜单 */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {/* 已登录用户菜单 */}
            {isLoggedIn && (
              <>
                {/* 真实彩票功能（已升级到 /real-lottery 模块） */}
                <MenuItem onClick={() => handleNavigate('/real-lottery/pick')}>🎫 领取彩票</MenuItem>
                <MenuItem onClick={() => handleNavigate('/real-lottery/orders')}>📋 我的彩票</MenuItem>
                <MenuItem onClick={() => handleNavigate('/real-lottery/redemptions')}>🎁 彩票兑奖</MenuItem>

                {/* 个人信息 - 根据系统跳转 */}
                {isLoggedInMongo ? (
                  <MenuItem onClick={() => handleNavigate('/profile-v2')}>👤 个人信息</MenuItem>
                ) : (
                  <MenuItem onClick={() => handleNavigate('/profile')}>👤 个人信息</MenuItem>
                )}

                <MenuItem onClick={() => handleNavigate('/settings')}>⚙️ 设置</MenuItem>
                <MenuItem divider />

                {/* 退出登录 */}
                <MenuItem
                  onClick={handleLogout}
                  sx={{ color: 'error.main' }}
                >
                  🚪 退出登录
                </MenuItem>
              </>
            )}

            {/* 未登录用户菜单（备用，实际上未登录时显示登录按钮而不是头像） */}
            {!isLoggedIn && (
              <MenuItem onClick={handleGoToLogin}>🔐 登录</MenuItem>
            )}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navigation;
